import { formatCurrency } from '../../utils/currency';
import type { ProviderLeaderboardEntry } from '../../types/dashboard';

interface ProviderLeaderboardProps {
  providers: ProviderLeaderboardEntry[];
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  periodLabel?: string;
  onRetry?: () => void;
}

export function ProviderLeaderboard({
  providers,
  loading = false,
  error = false,
  forbidden = false,
  periodLabel,
  onRetry,
}: ProviderLeaderboardProps) {

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Provider leaderboard</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Provider leaderboard</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load provider data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && providers.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-36 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-3 pb-3 space-y-2.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="h-3 w-24 bg-canvas animate-pulse rounded" />
              <div className="flex gap-3">
                <div className="h-3 w-8 bg-canvas animate-pulse rounded" />
                <div className="h-3 w-14 bg-canvas animate-pulse rounded" />
                <div className="h-3 w-8 bg-canvas animate-pulse rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (providers.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Provider leaderboard</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No provider activity in this period.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Provider leaderboard</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
      </div>

      {/* Table header */}
      <div className="px-4 pt-3 pb-1 flex items-center text-[10.5px] text-ink-4 font-medium uppercase tracking-wide">
        <span className="flex-1">Provider</span>
        <span className="w-14 text-right">Sessions</span>
        <span className="w-20 text-right">Revenue</span>
        <span className="w-12 text-right">Util</span>
      </div>

      {/* Provider rows */}
      <div className="px-4 pb-3 space-y-1.5">
        {providers.map((p, i) => {
          // Utilization color
          const utilColor = p.utilizationPct >= 80 ? 'text-sage' : p.utilizationPct >= 50 ? 'text-ink-2' : 'text-ink-4';

          return (
            <div key={p.providerId} className="flex items-center py-1">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[10.5px] text-ink-4 num w-4 shrink-0 text-right">{i + 1}</span>
                <span className="text-[12px] text-ink-2 truncate" title={p.name}>
                  {p.name}
                </span>
              </div>
              <span className="text-[12px] num w-14 text-right font-medium">
                {p.sessions}
              </span>
              <span className="text-[12px] num w-20 text-right font-medium">
                ${formatCurrency(p.revenue)}
              </span>
              <span className={`text-[12px] num w-12 text-right font-medium ${utilColor}`}>
                {p.utilizationPct.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
